<template>
  <svg
    :width="size"
    :height="height"
    viewBox="0 0 24 24"
    fill="none"
    :stroke="color"
    stroke-width="2"
    stroke-linecap="round"
    stroke-linejoin="round"
  >
    <component
      v-for="(element, index) in iconElements"
      :key="index"
      :is="element.tag"
      v-bind="element.attrs"
    />
  </svg>
</template>

<script lang="ts">
import Vue from 'vue';
import { icons } from 'lucide';

/**
 * Represents a Lucide icon element structure.
 * Lucide icons are arrays of [tag, attributes] tuples.
 */
interface LucideIconElement {
  tag: string;
  attrs: Record<string, string | number | boolean>;
}

/**
 * Raw Lucide icon format: [tag, attributes?]
 */
type LucideIconItem = [string, Record<string, string | number | boolean>?];

export default Vue.extend({
  name: 'LucideIcon',

  props: {
    name: {
      type: String,
      required: true
    },
    size: {
      type: [Number, String],
      default: 24
    },
    color: {
      type: String,
      default: 'currentColor'
    }
  },

  mounted() {
    // Warn about missing icon during lifecycle hook, not in computed
    const iconName = this.name as keyof typeof icons;
    const icon = icons[iconName];

    if (!icon) {
      console.warn(`Icon "${this.name}" not found in lucide icons`);
    }
  },

  computed: {
    height(): number | string {
      return this.size;
    },

    iconElements(): LucideIconElement[] {
      const iconName = this.name as keyof typeof icons;
      const icon = icons[iconName];

      if (!icon) {
        return [];
      }

      // Icons from lucide are arrays of [tag, attributes]
      // Example: ['path', { d: 'M...', fill: 'none' }]
      return icon.map((item: LucideIconItem): LucideIconElement => ({
        tag: item[0],
        attrs: item[1] || {}
      }));
    }
  }
});
</script>

<style scoped>
svg {
  display: inline-block;
  vertical-align: middle;
}
</style>
